import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { EventState, EventTransition } from 'stageclip-common';

import { createAddEventTransitionRequestedAction } from '../../actions/eventTransitionActions';
import * as PermissionsHelper from '../../helpers/permissionsHelper';
import { NormalizedEvent } from '../../models/normalized/normalizedEvent';
import Button from '../buttons/button';
import {
  baseMapDispatchToProps,
  baseMapStateToProps,
  ComponentBase,
  BaseDispatchProps,
  BaseStateProps,
} from '../componentBase';

import styles from './eventPage.module.scss';

interface AddEventTransitionButtonState {
  text: string;
  confirmMessage: string;
  className: string;
  disabled: boolean;
  shouldDisplay: boolean;
  transition: EventTransition;
}

interface AddEventTransitionButtonOwnProps {
  event: NormalizedEvent;
}

interface AddEventTransitionButtonDispatchProps extends BaseDispatchProps {
  createAddEventTransitionAction: (c: AddEventTransitionButton, eventTransition: EventTransition) => any;
}

class AddEventTransitionButton extends ComponentBase<
  AddEventTransitionButtonOwnProps,
  BaseStateProps,
  AddEventTransitionButtonDispatchProps
> {
  protected loggedInRender() {
    const buttonState = this.getButtonState();

    return buttonState.shouldDisplay ? (
      <Button
        text={buttonState.text}
        className={buttonState.className}
        disabled={buttonState.disabled}
        onClick={this.onClick(buttonState.confirmMessage, buttonState.transition)}
        icon={faArrowRight}
        allCapitals
      />
    ) : (
      <span />
    );
  }

  private onClick = (confirmMessage: string, transition: EventTransition) => () => {
    // eslint-disable-next-line no-alert
    if (window.confirm(confirmMessage)) {
      this.props.createAddEventTransitionAction(this, transition);
    }
  };

  private getButtonState(): AddEventTransitionButtonState {
    switch (this.props.event.state) {
      case EventState.WaitingForAssets:
        return {
          className: styles.disabledActionButton,
          confirmMessage: undefined,
          disabled: true,
          shouldDisplay:
            PermissionsHelper.userCan(PermissionsHelper.RestrictedActions.AccessAdminSection) ||
            this.props.event.assignee === this.props.currentUser.id,
          text: 'Start Progress',
          transition: undefined,
        };
      case EventState.ReadyToClip:
        return {
          className: styles.actionButton,
          confirmMessage:
            'Are you sure you want to start progress on this event? ' +
            'Please be aware that this action cannot be reverted and a record of this action will be taken.',
          disabled: false,
          shouldDisplay:
            PermissionsHelper.userCan(PermissionsHelper.RestrictedActions.AccessAdminSection) ||
            this.props.event.assignee === this.props.currentUser.id,
          text: 'Start Progress',
          transition: EventTransition.StartProcessing,
        };
      case EventState.ClippingInProgress:
        return {
          className: styles.actionButton,
          confirmMessage:
            'Are you sure you want to put in review the work done for this event? ' +
            'Please be aware that this action cannot be reverted and a record of this action will be taken. ' +
            'Someone else will review the work you have done.',
          disabled: false,
          shouldDisplay:
            PermissionsHelper.userCan(PermissionsHelper.RestrictedActions.AccessAdminSection) ||
            this.props.event.assignee === this.props.currentUser.id,
          text: 'Review',
          transition: EventTransition.PutInReview,
        };
      case EventState.ReviewNeeded:
        return {
          className: styles.actionButton,
          confirmMessage:
            'Are you sure you want to mark as complete the work for this event?' +
            'Please be aware that this action cannot be reverted and a record of this action will be taken.',
          disabled: false,
          shouldDisplay:
            PermissionsHelper.userCan(PermissionsHelper.RestrictedActions.AccessAdminSection) ||
            this.props.event.assignee !== this.props.currentUser.id,
          text: 'Complete Review',
          transition: EventTransition.MarkAsComplete,
        };
      default:
        return {
          className: undefined,
          confirmMessage: undefined,
          disabled: undefined,
          shouldDisplay: false,
          text: undefined,
          transition: undefined,
        };
    }
  }
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: any): AddEventTransitionButtonDispatchProps {
  return {
    ...baseMapDispatchToProps(dispatch),
    createAddEventTransitionAction: (c: AddEventTransitionButton, eventTransition: EventTransition) =>
      dispatch(createAddEventTransitionRequestedAction(ownProps.event.id, eventTransition, c, dispatch)),
  };
}

export default connect(baseMapStateToProps, mapDispatchToProps)(AddEventTransitionButton);
